import { Side } from '@diagram/model/node.model';
import { Group, Lane } from '@diagram/store/slices/resize.utils';
import { ResizeDragEvent } from '@xyflow/react';
import { NodeModel, Orientation } from '../model/node.model';

// recompute lane positions from sizes
export const layout = (group: Group): Group => {
  let p = group.start;
  const lanes = group.lanes.map((l) => {
    const ln = { ...l, pos: p };
    p += l.size + group.gutter;
    return ln;
  });
  return { ...group, lanes };
};

// clamp delta for resizing a pair of lanes
export const clampPairDelta = (a: Lane, b: Lane, deltaTowardB: number) => {
  // a grows by +delta, b shrinks by -delta
  const maxGrowA = Infinity;
  const maxShrinkB = b.size - b.min;
  const maxDelta = Math.min(maxGrowA, maxShrinkB);

  const minShrinkA = a.size - a.min;
  const minDelta = -minShrinkA; // if negative, a can shrink at most this magnitude

  return Math.max(minDelta, Math.min(deltaTowardB, maxDelta));
};

export const clampAllDelta = (lanes: Lane[], delta: number) => {
  // Everyone grows by +delta (if edge='end') or shrinks (if edge='start' with negative delta);
  // clamp so no lane violates min.
  if (delta >= 0) return delta; // growing is always fine
  const maxShrink = Math.min(...lanes.map((l) => l.size - l.min));
  return Math.max(delta, -maxShrink);
};

export const groupResizeFromStart = (group: Group, delta: number): Group => {
  const gNode = group.groupLane;
  const lane = group.lanes[0];

  gNode.size -= delta;
  gNode.pos += delta;
  lane.size -= delta;

  const otherLanes = group.lanes.slice(1).map((l) => ({
    ...l,
    pos: (l.pos -= delta),
  }));

  return { ...group, lanes: [gNode, lane, ...otherLanes] };
};

export const groupResizeFromEnd = (group: Group, delta: number): Group => {
  const gNode = group.groupLane;
  const lane = group.lanes[group.lanes.length - 1];

  gNode.size += delta;
  lane.size += delta;
  return { ...group, lanes: [gNode, lane] };
};

export const resizeAgainstNeighbor = (
  group: Group,
  i: number,
  toward: 'front' | 'back',
  delta: number
): Group => {
  const lanes = group.lanes;
  const a = lanes[i];
  const j = toward === 'front' ? i - 1 : i + 1;
  const b = lanes[j];
  if (!b) return layout(group); // no neighbor; fall back to group edge behavior

  //if front adjust before lanes size
  if (toward === 'front') {
    a.size -= delta;
    a.pos += delta;
    b.size += delta;
  }

  if (toward === 'back') {
    a.size += delta;
    b.size -= delta;
    b.pos += delta;
  }

  return { ...group, lanes: [a, b] };
};

export const equalizeAllTo = (group: Group, targetSize: number): Group => {
  const minAllowed = Math.max(...group.lanes.map((l) => l.min));
  const size = Math.max(targetSize, minAllowed);
  const lanes = group.lanes.map((l) => ({ ...l, size }));
  return layout({ ...group, lanes });
};

export const calculateSide = (
  e: ResizeDragEvent,
  rect: DOMRect,
  hit: number
): Side | null => {
  const x = e.sourceEvent.clientX;
  const y = e.sourceEvent.clientY;
  const onL = Math.abs(x - rect.left) <= hit;
  const onR = Math.abs(x - rect.right) <= hit;
  const onT = Math.abs(y - rect.top) <= hit;
  const onB = Math.abs(y - rect.bottom) <= hit;

  const raw: Side =
    onL && onT
      ? 'top-left'
      : onL && onB
      ? 'bottom-left'
      : onR && onT
      ? 'top-right'
      : onR && onB
      ? 'bottom-right'
      : onL
      ? 'left'
      : onR
      ? 'right'
      : onT
      ? 'top'
      : onB
      ? 'bottom'
      : '';

  return raw;
};

export const calculateSwimlaneRect = (
  orientation: Orientation,
  groupHeight: number,
  groupWidth: number,
  index: number
) => {
  switch (orientation) {
    case 'horizontal': {
      const laneHeight = groupHeight / 4;
      return {
        width: groupWidth,
        height: laneHeight,
        y: laneHeight * index,
        x: 0,
      };
    }
    case 'vertical': {
      const laneWidth = groupWidth / 4;
      return {
        width: laneWidth,
        height: groupHeight,
        x: laneWidth * index,
        y: 0,
      };
    }
  }
};

export const shiftSwimlanes = (
  lanes: NodeModel[],
  group: NodeModel,
  deltaX: number,
  deltaY: number
) => {
  const lanePatches = lanes.map((lane) => {
    return {
      id: lane.id,
      patch: {
        position: {
          x: lane.position.x + deltaX,
          y: lane.position.y + deltaY,
        },
      },
    };
  });
  const groupPatch = {
    id: group.id,
    patch: {
      width: group.width + deltaX,
      height: group.height + deltaY,
    },
  };

  return [...lanePatches, groupPatch];
};

export const resizeSwimlaneGroup = (
  swimlaneGroup: NodeModel,
  deltaX: number,
  deltaY: number
) => {
  const dimensions = {
    width: swimlaneGroup.width + deltaX,
    height: swimlaneGroup.height + deltaY,
  };
  return {
    id: swimlaneGroup.id,
    patch: {
      width: dimensions.width,
      height: dimensions.height,
    },
  };
};
